import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { getMicrosoftStatus, getMicrosoftAuthorizeUrl, disconnectMicrosoft } from '../services/microsoftIntegrationService';
import Icon from '../icon/newChart.svg';
import ScheduleIcon from '../icon/schedule.svg';
import BellIcon from '../icon/bell.svg';
import CloseSidebar from '../icon/closeSidebar.svg';
import LogoutIcon from '../icon/logout.svg';
import SettingsIcon from '../icon/settings.svg';
import CircleCloseIcon from '../icon/circle-close.svg';
import UserAvatar from '../icon/user-avatar.svg';
import { OPEN_SETTINGS_EVENT, SETTINGS_CLOSED_EVENT } from '../utils/settingsModal';
import { decodeJwtPayload, isTokenPcsUser } from '../utils/pcsUser';

const SETTINGS_NAV_ITEMS = [
  { id: 'general', label: '一般', icon: 'gear' },
  { id: 'connector', label: '第三方服務', icon: 'connector' },
];

const SETTINGS_APPEARANCE_OPTIONS = [
  { value: 'light', label: '淺色' },
  { value: 'dark', label: '深色' },
];

function readMygenieTokenRaw() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('mygenie_app');
    req.onerror = () => reject(new Error('Cannot open mygenie_app'));
    req.onsuccess = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('kv')) { db.close(); return resolve(undefined); }
      const getReq = db.transaction('kv', 'readonly').objectStore('kv').get('mygenie_token');
      getReq.onsuccess = (e) => { db.close(); resolve(e.target.result); };
      getReq.onerror = () => { db.close(); reject(new Error('Cannot read mygenie_token')); };
    };
  });
}

if (typeof window !== 'undefined') {
  window.__decodeJwtPayload = decodeJwtPayload;
  window.__isTokenPcsUser = isTokenPcsUser;
  window.__readMygenieTokenRaw = () => readMygenieTokenRaw().then(v => { console.log('[mygenie_token raw]', v); return v; });
}

/** 觸控起點在這些選擇器內時，不觸發全頁水平滑以切換手機側欄（避免與橫捲內容衝突） */
const HORIZONTAL_SCROLL_GESTURE_EXCLUDE_SELECTORS = [];

const SCHEDULE_MENU_ITEM_STORAGE_KEY = '__scheduleMenuItemVisible';

function SettingsCustomSelect({ id, labelId, value, onChange, options, triggerVariant = 'pill' }) {
  const [open, setOpen] = useState(false);
  const [listFixedStyle, setListFixedStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRootRef = useRef(null);

  const measureListPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setListFixedStyle({
      position: 'fixed',
      top: `${Math.round(r.bottom + 6)}px`,
      right: `${Math.round(document.documentElement.clientWidth - r.right)}px`,
      minWidth: `${Math.round(r.width)}px`,
      zIndex: 10002,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setListFixedStyle(null);
      return;
    }
    measureListPosition();
    window.addEventListener('resize', measureListPosition);
    return () => window.removeEventListener('resize', measureListPosition);
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || listRootRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value) ?? options[0];

  const triggerClass =
    triggerVariant === 'plain'
      ? 'settings-custom-select-trigger settings-custom-select-trigger--plain'
      : 'settings-custom-select-trigger settings-custom-select-trigger--pill';

  return (
    <div
      className={`settings-custom-select ${triggerVariant === 'plain' ? 'settings-custom-select--plain' : ''}`}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="settings-custom-select-trigger-text">{current.label}</span>
        <svg
          className="settings-custom-select-chevron-svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.25L6 7.75l3.5-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        listFixedStyle &&
        createPortal(
          <ul
            ref={listRootRef}
            className="settings-custom-select-list settings-custom-select-list--portal"
            style={listFixedStyle}
            role="listbox"
            aria-labelledby={labelId}
          >
            {options.map((opt) => {
              const selected = value === opt.value;
              return (
                <li key={opt.value} className="settings-custom-select-li" role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`settings-custom-select-option ${selected ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <span className="settings-custom-select-option-label">{opt.label}</span>
                    {selected && (
                      <svg
                        className="settings-custom-select-check"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          fill="currentColor"
                          d="M9 16.17L4.83 12l-1.42 1.41L9 19l12-12-1.41-1.41z"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
}

function ConnectorServiceIcon({ type }) {
  switch (type) {
    case 'outlook':
      /* Fluent-style Outlook: blue bg, white envelope body, blue fold line */
      return (
        <svg className="connector-service-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none">
          <rect width="24" height="24" rx="4" fill="#0078D4"/>
          {/* envelope body */}
          <rect x="3.5" y="7.5" width="17" height="11" rx="1.5" fill="white"/>
          {/* fold / flap line */}
          <path d="M3.5 9.5 L12 14.5 L20.5 9.5" stroke="#0078D4" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'onedrive':
      /* Fluent-style OneDrive: dark-blue bg, white cloud */
      return (
        <svg className="connector-service-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none">
          <rect width="24" height="24" rx="4" fill="#0364B8"/>
          {/* cloud path — large arc top-right, small arc bottom-left */}
          <path
            d="M14.5 8.5 A5 5 0 0 0 5.1 12.1 A3 3 0 0 0 6 18 H18 A3.5 3.5 0 0 0 18 11 A5 5 0 0 0 14.5 8.5 Z"
            fill="white"
          />
        </svg>
      );
    case 'calendar':
      /* Fluent-style Calendar: blue header band, white body, grid lines, date */
      return (
        <svg className="connector-service-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none">
          <rect width="24" height="24" rx="4" fill="#0078D4"/>
          {/* white calendar body */}
          <rect x="3.5" y="6" width="17" height="14.5" rx="1.5" fill="white"/>
          {/* blue header band */}
          <path d="M3.5 6 H20.5 V10 H3.5 Z" fill="#0078D4"/>
          {/* round top corners of band to match outer rect */}
          <rect x="3.5" y="6" width="17" height="4" rx="1.5" fill="#0078D4"/>
          {/* binding hooks */}
          <line x1="8.5" y1="4" x2="8.5" y2="8" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
          <line x1="15.5" y1="4" x2="15.5" y2="8" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
          {/* date number "15" */}
          <text x="7.8" y="18.5" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="6.5" fill="#0078D4">15</text>
        </svg>
      );
    case 'contacts':
      /* Fluent-style People: teal bg, white head + shoulder arc */
      return (
        <svg className="connector-service-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none">
          <rect width="24" height="24" rx="4" fill="#0F6CBD"/>
          {/* head */}
          <circle cx="12" cy="9.5" r="3.2" fill="white"/>
          {/* shoulders */}
          <path d="M5.5 20 C5.5 16.13 8.46 13 12 13 C15.54 13 18.5 16.13 18.5 20" fill="white"/>
        </svg>
      );
    default:
      return null;
  }
}

function SettingsNavIcon({ type }) {
  switch (type) {
    case 'gear':
      return <img src={SettingsIcon} alt="" className="settings-modal-nav-icon" />;
    case 'connector':
      return (
        <svg className="settings-modal-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20.5 11H19V7a2 2 0 0 0-2-2h-4V3.5a2.5 2.5 0 0 0-5 0V5H4a2 2 0 0 0-2 2v4h1.5a2.5 2.5 0 0 1 0 5H2v4a2 2 0 0 0 2 2h4v-1.5a2.5 2.5 0 0 1 5 0V21h4a2 2 0 0 0 2-2v-4h-1.5a2.5 2.5 0 0 1 0-5z" />
        </svg>
      );
    default:
      return null;
  }
}

const Sidebar = ({ 
  sidebarOpen, 
  setSidebarOpen, 
  isMobile,
  mobileMenuOpen,
  setMobileMenuOpen,
  chatHistory, 
  createNewChat, 
  switchToChat, 
  deleteChat,
  currentChatId,
  user,
  onLogout,
  openNewChat,
  theme,
  setTheme,
  activeView,
  scheduleListOpen,
  onOpenSchedule,
  onOpenMailNotification,
  showMailNotification = false
}) => {
  const [searchText, setSearchText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatIdToDelete, setChatIdToDelete] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [activeSettingsNav, setActiveSettingsNav] = useState('general');
  const [connectorStatuses, setConnectorStatuses] = useState(null);
  const [connectorStatusError, setConnectorStatusError] = useState(false);
  const [isPcsUser, setIsPcsUser] = useState(null);
  const [pendingConnectors, setPendingConnectors] = useState({});
  const [msIntegrationStatus, setMsIntegrationStatus] = useState(null);

  // 建立排程選單項目預設顯示；若使用者曾透過 DevTools 明確關閉過(存了 'false')則尊重該選擇，
  // 仍可從 DevTools 呼叫 __toggleScheduleMenuItem() 開關。
  const [showScheduleMenuItem, setShowScheduleMenuItem] = useState(() => {
    try {
      return localStorage.getItem(SCHEDULE_MENU_ITEM_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  // 從 DevTools 呼叫 __toggleScheduleMenuItem() 顯示/隱藏「建立排程」選單，也可傳 true/false 指定狀態
  useEffect(() => {
    window.__toggleScheduleMenuItem = (visible) => {
      setShowScheduleMenuItem((prev) => {
        const next = typeof visible === 'boolean' ? visible : !prev;
        try {
          localStorage.setItem(SCHEDULE_MENU_ITEM_STORAGE_KEY, String(next));
        } catch {
          // 忽略 localStorage 寫入失敗
        }
        return next;
      });
    };
    return () => {
      delete window.__toggleScheduleMenuItem;
    };
  }, []);

  const handleOpenSettings = () => {
    setShowSettingsModal(true);
    setConnectorStatuses(null);
    setConnectorStatusError(false);
    setIsPcsUser(null);
    readMygenieTokenRaw()
      .then((raw) => {
        const pcs = isTokenPcsUser(raw);
        setIsPcsUser(pcs);
        if (!pcs || isMobile) return;
        return getMicrosoftStatus();
      })
      .then((res) => res && setMsIntegrationStatus(res))
      .catch(() => {
        setIsPcsUser(prev => prev ?? false);
        setConnectorStatusError(true);
        setMsIntegrationStatus(null);
      });
  };

  // 其他頁面（例如信件即時通知）用 openSettings(tab) 打開設定視窗並直接切到指定分頁
  const openSettingsFromEventRef = useRef(null);
  openSettingsFromEventRef.current = (tab) => {
    handleOpenSettings();
    setActiveSettingsNav(tab || 'general');
  };
  useEffect(() => {
    const onOpen = (event) => openSettingsFromEventRef.current(event.detail?.tab);
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);

  // 關閉時通知頁面重查狀態：使用者可能剛在「第三方服務」連結或解除了 Outlook
  const settingsWasOpenRef = useRef(false);
  useEffect(() => {
    if (settingsWasOpenRef.current && !showSettingsModal) {
      window.dispatchEvent(new Event(SETTINGS_CLOSED_EVENT));
    }
    settingsWasOpenRef.current = showSettingsModal;
  }, [showSettingsModal]);

  useEffect(() => {
    if (!msIntegrationStatus) return;
    const types = msIntegrationStatus.data?.types;
    if (!types) {
      setConnectorStatusError(true);
      return;
    }
    const SERVICES = ['outlook', 'onedrive', 'calendar', 'contacts'];
    const statuses = {};
    for (const id of SERVICES) {
      statuses[id] = types[id]?.status === 'active' ? 'connected' : 'disconnected';
    }
    setConnectorStatusError(false);
    setConnectorStatuses(statuses);
  }, [msIntegrationStatus]);

  const handleConnectorAction = (id, action) => {
    if (action === 'disconnect') {
      setPendingConnectors(prev => ({ ...prev, [id]: true }));
      disconnectMicrosoft(id)
        .then((res) => {
          if (res?.code !== 200) {
            console.error(`[Connector] disconnect ${id} failed:`, res?.code, res?.message);
            return;
          }
          setConnectorStatuses(prev => ({ ...prev, [id]: 'disconnected' }));
        })
        .catch((err) => {
          console.error(`[Connector] disconnect ${id} failed:`, err?.message);
        })
        .finally(() => {
          setPendingConnectors(prev => ({ ...prev, [id]: false }));
        });
      return;
    }
    setPendingConnectors(prev => ({ ...prev, [id]: true }));
    getMicrosoftAuthorizeUrl(id)
      .then((res) => {
        const url = res?.data?.url ?? res?.url;
        if (!url) {
          console.error(`[Connector] authorize-url ${id}: no url in response`, res);
          setPendingConnectors(prev => ({ ...prev, [id]: false }));
          return;
        }
        const authWindow = window.open(url, '_blank');
        console.log(`[Connector] opened auth window for ${id}, listening for postMessage...`);

        let finished = false;

        const clearPending = () => setPendingConnectors(prev => ({ ...prev, [id]: false }));

        const onSuccess = (source) => {
          if (finished) return;
          finished = true;
          console.log(`[Connector] OAuth success via ${source}, refreshing status...`);
          window.removeEventListener('message', onMessage);
          clearInterval(pollInterval);
          if (authWindow && !authWindow.closed) authWindow.close();
          clearPending();
          getMicrosoftStatus()
            .then((statusRes) => setMsIntegrationStatus(statusRes))
            .catch(() => {});
        };

        // postMessage（callback 頁面主動通知）
        const onMessage = (event) => {
          if (event.data?.type !== 'genaius:integrations:microsoft-oauth-success') return;
          onSuccess('postMessage');
        };
        window.addEventListener('message', onMessage);

        // polling fallback（每 3 秒查一次狀態，供 postMessage 無法送達時使用）
        // connectorStatuses 存的是 'connected' / 'disconnected'（見上方 msIntegrationStatus effect），不是後端的 'active'
        const wasConnected = connectorStatuses?.[id] === 'connected';
        const pollInterval = setInterval(() => {
          getMicrosoftStatus()
            .then((statusRes) => {
              const newStatus = statusRes?.data?.types?.[id]?.status;
              if (newStatus === 'active' && !wasConnected) {
                onSuccess('polling');
              }
            })
            .catch(() => {});
        }, 3000);

        // 5 分鐘後強制清除
        setTimeout(() => {
          if (finished) return;
          finished = true;
          window.removeEventListener('message', onMessage);
          clearInterval(pollInterval);
          clearPending();
        }, 5 * 60 * 1000);
      })
      .catch((err) => {
        console.error(`[Connector] authorize-url ${id} failed:`, err?.message);
        setPendingConnectors(prev => ({ ...prev, [id]: false }));
      });
  };
  const handleNewChat = () => {
    createNewChat();
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleOpenSchedule = () => {
    onOpenSchedule?.();
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleOpenMailNotification = () => {
    onOpenMailNotification?.();
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleChatClick = (chatId) => {
    switchToChat(chatId);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // 手機端觸摸事件處理
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchStart = (e) => {
      const el = e.target;
      if (el && typeof el.closest === 'function') {
        for (const sel of HORIZONTAL_SCROLL_GESTURE_EXCLUDE_SELECTORS) {
          if (el.closest(sel)) {
            return;
          }
        }
      }

      // 記錄觸摸開始位置
      const touch = e.touches[0];
      const startX = touch.clientX;
      const startY = touch.clientY;
      
      const handleTouchMove = (e) => {
        const touch = e.touches[0];
        const currentX = touch.clientX;
        const currentY = touch.clientY;
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        
        // 如果是水平滑動且距離足夠
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
          // 從左往右滑動自動打開選單的功能已停用（Rock資訊長使用不慣）
          // if (deltaX > 0 && !mobileMenuOpen) {
          //   // 從左邊滑入，打開菜單
          //   setMobileMenuOpen(true);
          // } else
          if (deltaX < 0 && mobileMenuOpen) {
            // 從右邊滑出，關閉菜單
            setMobileMenuOpen(false);
          }
        }
      };

      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchstart', handleTouchStart);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [isMobile, mobileMenuOpen]);

  const handleDeleteChat = (e, chatId) => {
    e.stopPropagation();
    setChatIdToDelete(chatId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (chatIdToDelete) {
      deleteChat(chatIdToDelete);
      setShowDeleteConfirm(false);
      setChatIdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setChatIdToDelete(null);
  };

  // 刪除確認對話框開啟時，按 Enter 直接確定刪除
  useEffect(() => {
    if (!showDeleteConfirm) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmDelete();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteConfirm, chatIdToDelete]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    // 重新登入後須收合選單
    if (isMobile) {
      setMobileMenuOpen(false);
    }
    setShowLogoutConfirm(false);
    setShowSettingsModal(false);
    onLogout();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const filteredChats = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const list = (chatHistory || []).filter(chat => !chat.isTemporary && !chat.isDeleted);
    const sorted = [...list].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return dateB - dateA; // 最新在前
    });
    if (!q) return sorted;
    return sorted.filter(chat => {
      const inTitle = (chat.title || '').toLowerCase().includes(q);
      const inMessages = Array.isArray(chat.messages)
        ? chat.messages.some(m => (m.text || '').toLowerCase().includes(q))
        : false;
      return inTitle || inMessages;
    });
  }, [chatHistory, searchText]);

  // AGUI-1304：停在空白新對話時「新對話」要反白，跟排程管理、信件即時通知一樣。
  // 新對話是前端暫存對話（isTemporary，送出第一則訊息才升級成正式對話），不會出現在最近對話裡，
  // 所以找不到目前對話或目前對話仍是暫存時，就算停在新對話。
  const currentChat = chatHistory?.find(chat => chat.id === currentChatId);
  const onNewChat = activeView === 'chat' && (!currentChat || currentChat.isTemporary);

  // 取得要刪除的聊天標題
  const chatToDelete = chatHistory?.find(chat => chat.id === chatIdToDelete);
  const deleteChatTitle = chatToDelete?.title || '新對話';

  return (
    <>
      {/* 刪除確認對話框 */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelDelete}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="delete-confirm-title">刪除對話?</div>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-btn cancel-btn" onClick={cancelDelete}>
                取消
              </button>
              <button className="delete-confirm-btn confirm-btn" onClick={confirmDelete}>
                刪除
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelLogout}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-title">確定要登出嗎？</div>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-btn cancel-btn" onClick={cancelLogout}>
                取消
              </button>
              <button className="delete-confirm-btn confirm-btn" onClick={confirmLogout}>
                登出
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 設定視窗（僅版面，無實際功能） */}
      {showSettingsModal && (
        <div
          className="settings-modal-overlay"
          onClick={() => setShowSettingsModal(false)}
          role="presentation"
        >
          <div
            className="settings-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-modal-title"
          >
            <div className="settings-modal-inner">
              <aside className="settings-modal-sidebar">
                <button
                  type="button"
                  className="settings-modal-close-x"
                  onClick={() => setShowSettingsModal(false)}
                  aria-label="關閉設定"
                >
                  ×
                </button>
                <div className="settings-modal-sidebar-stack">
                  <nav className="settings-modal-nav" aria-label="設定分類">
                    {SETTINGS_NAV_ITEMS.filter((item) => item.id !== 'connector' || isPcsUser === true).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`settings-modal-nav-item${activeSettingsNav === item.id ? ' active' : ''}`}
                        onClick={() => setActiveSettingsNav(item.id)}
                      >
                        <SettingsNavIcon type={item.icon} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>
              <div className="settings-modal-main">
                <div className="settings-modal-main-top">
                  {activeSettingsNav === 'general' && (
                    <>
                      <h2 id="settings-modal-title" className="settings-modal-title">
                        一般
                      </h2>
                      <div className="settings-modal-rows">
                        <div className="settings-modal-row settings-modal-row-has-select">
                          <span className="settings-modal-label" id="settings-appearance-label">
                            外觀
                          </span>
                          <SettingsCustomSelect
                            id="settings-appearance-select"
                            labelId="settings-appearance-label"
                            value={theme === 'dark' || theme === 'light' ? theme : 'light'}
                            onChange={setTheme}
                            options={SETTINGS_APPEARANCE_OPTIONS}
                            triggerVariant="pill"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {activeSettingsNav === 'connector' && (
                    <>
                      <h2 id="settings-modal-title" className="settings-modal-title">
                        第三方服務
                      </h2>
                      {isMobile ? (
                        <p className="connector-mobile-notice">
                          為了守護您的個人資料安全，第三方服務（如 Outlook、OneDrive、Calendar、Contacts）的連結設定，目前只限在『網頁版』進行。
                        </p>
                      ) : (
                        <>
                          <p className="connector-description">
                            連結您的 Microsoft 365 帳號以整合相關服務。
                          </p>
                          <div className="settings-modal-rows">
                            {[
                              { id: 'outlook',  label: 'Outlook',  enabled: true },
                              { id: 'onedrive', label: 'OneDrive', enabled: true },
                              { id: 'calendar', label: 'Calendar', enabled: true },
                              { id: 'contacts', label: 'Contacts', enabled: true },
                            ].filter(item => item.enabled).map(({ id, label }) => {
                              const status = connectorStatuses ? connectorStatuses[id] : null;
                              const isPending = !!pendingConnectors[id];
                              const disabled = isPending;
                              return (
                                <div key={id} className="settings-modal-row">
                                  <span className="settings-modal-label connector-service-label">
                                    <ConnectorServiceIcon type={id} />
                                    {label}
                                  </span>
                                  <div className="connector-pill-group">
                                    {connectorStatusError ? (
                                      <span className="connector-status-error">
                                        取得狀態失敗
                                      </span>
                                    ) : status === null && (
                                      <span className="connector-status-loading">
                                        <span className="connector-status-loading-dot" />
                                        狀態取得中
                                      </span>
                                    )}
                                    {status === 'disconnected' && (
                                      <button
                                        type="button"
                                        className={`connector-pill connector-pill--connect${isPending ? ' connector-pill--pending' : ''}`}
                                        onClick={() => handleConnectorAction(id, 'connect')}
                                        disabled={disabled}
                                      >
                                        {isPending ? '授權中...' : '連結'}
                                      </button>
                                    )}
                                    {status === 'connected' && (
                                      <>
                                        <button
                                          type="button"
                                          className={`connector-pill connector-pill--reconnect${isPending ? ' connector-pill--pending' : ''}`}
                                          onClick={() => handleConnectorAction(id, 'reconnect')}
                                          disabled={disabled}
                                        >
                                          {isPending ? '授權中...' : '重新連結'}
                                        </button>
                                        <button
                                          type="button"
                                          className={`connector-pill connector-pill--disconnect${isPending ? ' connector-pill--pending' : ''}`}
                                          onClick={() => handleConnectorAction(id, 'disconnect')}
                                          disabled={disabled}
                                        >
                                          {isPending ? '中斷中...' : '解除連結'}
                                        </button>
                                      </>
                                    )}
                                    {status === 'reconnect' && (
                                      <button
                                        type="button"
                                        className={`connector-pill connector-pill--connect${isPending ? ' connector-pill--pending' : ''}`}
                                        onClick={() => handleConnectorAction(id, 'connect')}
                                        disabled={disabled}
                                      >
                                        {isPending ? '授權中...' : '連結'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                {!isMobile && activeSettingsNav === 'general' && (
                  <div className="settings-modal-logout-wrap">
                    <button
                      type="button"
                      className="settings-modal-logout-btn"
                      onClick={handleLogout}
                    >
                      <img src={LogoutIcon} alt="" className="settings-modal-nav-icon" />
                      <span>登出</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 手機版側欄展開時的背景遮罩 */}
      {isMobile && mobileMenuOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* 手機端漢堡菜單按鈕：手機版離開目前頁面、回到全域選單（新對話、對話紀錄、設定等）
          的入口，排程頁自己的清單抽屜開關（開排程清單）管的是不同的抽屜，不能互相取代，
          所以正常情況下（含排程頁、清單抽屜關閉時）都要渲染。
          AGUI-1169：只有排程頁的清單抽屜本身已經打開時才隱藏這顆——那個抽屜跟這顆按鈕
          要開的全域選單，兩個都是從左邊滑出的抽屜，同時開會疊在一起，且清單抽屜自己就有
          X 關閉鈕，不缺這顆的替代路徑。 */}
      {isMobile && !(activeView === 'schedule' && scheduleListOpen) && (
        <button
          className={`mobile-menu-btn ${mobileMenuOpen ? 'hide' : 'show'}`}
          onClick={handleMobileMenuToggle}
        >
          <div className="tooltip-wrapper tooltip-right">
            <img src={CloseSidebar} alt="icon"/>
          </div>
        </button>
      )}

   
      <div className={`sidebar ${isMobile ? (mobileMenuOpen ? 'mobile-open' : 'mobile-closed') : (sidebarOpen ? 'open' : 'collapsed')}`}>
        {/* 固定頂部 */}
        <div className="sidebar-top">
          <div className="sidebar-header">
            
            {/* 網頁版按鈕 */}
            {/*!isMobile && (
              <div className="icon-wrap">
                <img 
                  src="/logo-icon.png" 
                  alt="LogoIcon" 
                  className={`logo-icon ${sidebarOpen ? '' : 'clickable'} `}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                />
              </div>
            )*/}

            {!isMobile && (
              <div className="tooltip-wrapper">
               <img 
               className={`toggle-btn ${sidebarOpen ? 'show' : 'hide'}`}
                onClick={() => setSidebarOpen(!sidebarOpen)}
               src={CloseSidebar} alt="icon" />
                <span className="tooltip">
                  {sidebarOpen ? "收合選單" : "展開選單"}
                </span>
              </div>
            )}


            {/* 手機版按鈕 */}
            {/*isMobile && (
              <img 
                src="/logo-icon.png" 
                alt="LogoIcon" 
                className={`logo-icon ${sidebarOpen ? '' : 'clickable'} `}
                width={25}
                onClick={handleMobileMenuToggle}
              />
            )*/}
            {isMobile && (
              <div className="tooltip-wrapper tooltip-right">
               <img 
               className={`toggle-btn ${mobileMenuOpen ? 'show' : 'hide'}`}
               onClick={handleMobileMenuToggle}
               src={CloseSidebar} alt="icon" />
              </div>
             
            )}
          </div>

        <div className="menu">
          <div 
            className={`menu-item new-chat-item ${onNewChat ? 'active' : ''}`}
            onClick={handleNewChat}
          >
            <img src={Icon} alt="icon" />
            <span className="label">新對話</span>
            <span className="new-chat-shortcut">Ctrl+Shift+O</span>
            {!isMobile && !sidebarOpen && (
              <span className="tooltip">新對話 Ctrl+Shift+O</span>
            )}
          </div>
          {showScheduleMenuItem && (
            <div
              className={`menu-item schedule-item ${activeView === 'schedule' ? 'active' : ''}`}
              onClick={handleOpenSchedule}
            >
              <img src={ScheduleIcon} alt="icon" />
              {/* AGUI-1165（Reopened）：改成「排程管理」（名詞，跟其他選單項目的命名方式一致），
                  不再是「管理排程」（動詞片語）——MyBro 對話裡引導使用者去查看執行結果、
                  管理已建立的排程時，說的也是「排程管理」，兩邊要對得起來，使用者才找得到。
                  第一次修復只改了 AI 回覆文字、沒改這裡的實際按鈕文字，QA 複測後判定沒修好、
                  reopen；這次兩邊一起改。 */}
              <span className="label">排程管理</span>
              <span className="beta-pill">BETA</span>
              {!isMobile && !sidebarOpen && (
                <span className="tooltip">排程管理</span>
              )}
            </div>
          )}
          {/* 信件即時通知（AGUI-1113）。BETA：訂閱維持與規則判斷仍在驗證中。
              只開放 PCS domain（10-20）：非 PCS 連不了 Outlook，進來只會一直看到「未連結」 */}
          {showMailNotification && (
            <div
              className={`menu-item mail-notification-item ${activeView === 'mail-notification' ? 'active' : ''}`}
              onClick={handleOpenMailNotification}
            >
              <img src={BellIcon} alt="icon" />
              <span className="label">信件即時通知</span>
              <span className="beta-pill">BETA</span>
              {!isMobile && !sidebarOpen && (
                <span className="tooltip">信件即時通知</span>
              )}
            </div>
          )}
        </div>

        {/* 聊天歷史列表 */}
        <div className="chat-history">
          {(sidebarOpen || (isMobile && mobileMenuOpen)) && (
            <>
              <div className="search-box" style={{ padding: '4px 12px 8px 12px', position: 'relative' }}>
                <input
                  type="text"
                  placeholder="搜尋對話...（標題、訊息）"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: '100%', padding: '8px 32px 8px 10px', borderRadius: 6, border: '1px solid #ddd' }}
                />
                {searchText && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchText('')}
                    aria-label="清除搜尋"
                  >
                    <img src={CircleCloseIcon} alt="清除" style={{ width: '16px', height: '16px' }} />
                  </button>
                )}
              </div>
              
              {filteredChats && filteredChats.length > 0 && (
                <div className="section-title">最近對話</div>
              )}
              <div className="chat-list">
                {searchText.trim() && filteredChats.length === 0 ? (
                  <div className="section-title">查無結果</div>
                ) : (
                  filteredChats && filteredChats.map((chat) => (
                    <div
                      key={chat.id}
                      className={`chat-item ${currentChatId === chat.id && activeView === 'chat' ? 'active' : ''}`}
                      onClick={() => handleChatClick(chat.id)}
                    >
                      <div className="chat-content">
                        <div className="chat-title">{chat.title || '新對話'}</div>
                      </div>
                      <button
                        className="delete-chat-btn"
                        onClick={(e) => handleDeleteChat(e, chat.id)}
                        title="刪除對話"
                      >
                        🗑️
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* 貼底 */}
        <div className="user-info">
          <div className="user-details">
            <span className="user-icon">
            <img src={UserAvatar} alt="清除" style={{ width: '16px', height: '16px' }} />
            </span>
            {(sidebarOpen || (isMobile && mobileMenuOpen)) && (
              <div className="user-text">
                <div className="username">{user?.username || '未知用戶'}</div>
              </div>
            )}
          </div>
          {(sidebarOpen || (isMobile && mobileMenuOpen)) && (
            <>
              {/* <img src={LogoutIcon} alt="icon" onClick={handleLogout}
                title="登出" className="logout-btn" /> */}
              <button
                type="button"
                className="settings-btn"
                onClick={handleOpenSettings}
                title="設定"
                aria-label="設定"
              >
                <img src={SettingsIcon} alt="" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
